'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import { useTranslation } from '@/hooks/useTranslation';

interface DocSection {
    id: string;
    title: string;
    icon: string;
    content: React.ReactNode;
}

const DocsPage = () => {
    const { t } = useTranslation();
    const [activeSection, setActiveSection] = useState('overview');

    const docSections: DocSection[] = [
        {
            id: 'overview',
            title: 'Platform Overview',
            icon: 'InformationCircleIcon',
            content: (
                <div className="space-y-6">
                    <div>
                        <h2 className="text-3xl font-headline font-bold text-foreground mb-4">
                            Welcome to EgypTex Gateway
                        </h2>
                        <p className="text-lg text-muted-foreground leading-relaxed mb-4">
                            EgypTex Gateway is Egypt's premier B2B textile manufacturing platform, connecting global buyers with verified Egyptian textile manufacturers. We specialize in premium Egyptian cotton products and comprehensive textile solutions.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                        <div className="bg-card border border-border rounded-lg p-6">
                            <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                                <Icon name="UserGroupIcon" size={24} variant="outline" className="text-primary" />
                            </div>
                            <h3 className="text-xl font-headline font-bold text-foreground mb-2">
                                For Buyers
                            </h3>
                            <p className="text-muted-foreground">
                                Access a curated network of verified manufacturers, browse extensive product catalogs, and streamline your sourcing process with our advanced platform.
                            </p>
                        </div>

                        <div className="bg-card border border-border rounded-lg p-6">
                            <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                                <Icon name="BuildingOfficeIcon" size={24} variant="outline" className="text-primary" />
                            </div>
                            <h3 className="text-xl font-headline font-bold text-foreground mb-2">
                                For Manufacturers
                            </h3>
                            <p className="text-muted-foreground">
                                Showcase your products to a global audience, manage your digital presence, and connect with qualified buyers through our comprehensive vendor dashboard.
                            </p>
                        </div>
                    </div>

                    <div className="bg-gradient-to-r from-primary/5 to-primary/10 border border-primary/20 rounded-lg p-6">
                        <h3 className="text-xl font-headline font-bold text-foreground mb-3">
                            Key Features
                        </h3>
                        <ul className="grid md:grid-cols-2 gap-3">
                            <li className="flex items-start gap-2">
                                <Icon name="CheckCircleIcon" size={20} variant="solid" className="text-primary mt-0.5" />
                                <span className="text-muted-foreground">Verified manufacturer profiles</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <Icon name="CheckCircleIcon" size={20} variant="solid" className="text-primary mt-0.5" />
                                <span className="text-muted-foreground">Advanced product search & filtering</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <Icon name="CheckCircleIcon" size={20} variant="solid" className="text-primary mt-0.5" />
                                <span className="text-muted-foreground">Direct inquiry management</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <Icon name="CheckCircleIcon" size={20} variant="solid" className="text-primary mt-0.5" />
                                <span className="text-muted-foreground">Multi-language support</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <Icon name="CheckCircleIcon" size={20} variant="solid" className="text-primary mt-0.5" />
                                <span className="text-muted-foreground">Quality certification tracking</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <Icon name="CheckCircleIcon" size={20} variant="solid" className="text-primary mt-0.5" />
                                <span className="text-muted-foreground">Secure communication channels</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <Icon name="CheckCircleIcon" size={20} variant="solid" className="text-primary mt-0.5" />
                                <span className="text-muted-foreground"><strong>8 textile-specific</strong> product categories</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <Icon name="CheckCircleIcon" size={20} variant="solid" className="text-primary mt-0.5" />
                                <span className="text-muted-foreground"><strong>Tiered pricing</strong> in Egyptian Pounds (EGP)</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <Icon name="CheckCircleIcon" size={20} variant="solid" className="text-primary mt-0.5" />
                                <span className="text-muted-foreground"><strong>Product videos</strong> (up to 3 per product)</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <Icon name="CheckCircleIcon" size={20} variant="solid" className="text-primary mt-0.5" />
                                <span className="text-muted-foreground"><strong>Manufacturer ratings</strong> & response times</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <Icon name="CheckCircleIcon" size={20} variant="solid" className="text-primary mt-0.5" />
                                <span className="text-muted-foreground"><strong>Sample service</strong> pricing</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <Icon name="CheckCircleIcon" size={20} variant="solid" className="text-primary mt-0.5" />
                                <span className="text-muted-foreground"><strong>Google OAuth</strong> sign-in & sign-up</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <Icon name="CheckCircleIcon" size={20} variant="solid" className="text-primary mt-0.5" />
                                <span className="text-muted-foreground"><strong>Real-time statistics</strong> & analytics</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <Icon name="CheckCircleIcon" size={20} variant="solid" className="text-primary mt-0.5" />
                                <span className="text-muted-foreground"><strong>Automated email</strong> notifications</span>
                            </li>
                        </ul>
                    </div>
                </div>
            ),
        },
        {
            id: 'getting-started',
            title: 'Getting Started',
            icon: 'RocketLaunchIcon',
            content: (
                <div className="space-y-6">
                    <div>
                        <h2 className="text-3xl font-headline font-bold text-foreground mb-4">
                            Getting Started with EgypTex Gateway
                        </h2>
                        <p className="text-lg text-muted-foreground leading-relaxed mb-6">
                            Follow these simple steps to start sourcing premium Egyptian textiles or showcase your manufacturing capabilities.
                        </p>
                    </div>

                    <div className="space-y-6">
                        <div className="bg-card border border-border rounded-lg p-6">
                            <div className="flex items-start gap-4">
                                <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center flex-shrink-0">
                                    <span className="text-white font-bold">1</span>
                                </div>
                                <div className="flex-1">
                                    <h3 className="text-xl font-headline font-bold text-foreground mb-2">
                                        Create Your Account
                                    </h3>
                                    <p className="text-muted-foreground mb-3">
                                        Sign up as a buyer or manufacturer using email or Google. Choose your preferred method for quick and secure registration.
                                    </p>
                                    <div className="bg-muted/30 rounded-lg p-4 mb-3">
                                        <h4 className="font-semibold text-foreground mb-2 flex items-center gap-2">
                                            <Icon name="SparklesIcon" size={16} variant="solid" className="text-primary" />
                                            New: Sign Up with Google
                                        </h4>
                                        <p className="text-sm text-muted-foreground mb-2">
                                            Use your Google account for instant sign-up with automatic email verification. No need to remember another password!
                                        </p>
                                        <ul className="text-sm text-muted-foreground space-y-1 ml-4">
                                            <li>• One-click authentication</li>
                                            <li>• Automatic email verification</li>
                                            <li>• Secure OAuth 2.0 protocol</li>
                                        </ul>
                                    </div>
                                    <Link
                                        href="/signup"
                                        className="inline-flex items-center gap-2 text-primary hover:text-primary/80 font-medium transition-colors"
                                    >
                                        Sign Up Now
                                        <Icon name="ArrowRightIcon" size={16} variant="outline" />
                                    </Link>
                                </div>
                            </div>
                        </div>

                        <div className="bg-card border border-border rounded-lg p-6">
                            <div className="flex items-start gap-4">
                                <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center flex-shrink-0">
                                    <span className="text-white font-bold">2</span>
                                </div>
                                <div className="flex-1">
                                    <h3 className="text-xl font-headline font-bold text-foreground mb-2">
                                        Complete Your Profile
                                    </h3>
                                    <p className="text-muted-foreground mb-3">
                                        Add detailed information about your business, upload certifications, and showcase your capabilities or requirements.
                                    </p>
                                    <div className="flex flex-wrap gap-2">
                                        <span className="px-3 py-1 bg-muted rounded-full text-sm text-muted-foreground">
                                            Business Details
                                        </span>
                                        <span className="px-3 py-1 bg-muted rounded-full text-sm text-muted-foreground">
                                            Certifications
                                        </span>
                                        <span className="px-3 py-1 bg-muted rounded-full text-sm text-muted-foreground">
                                            Product Portfolio
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="bg-card border border-border rounded-lg p-6">
                            <div className="flex items-start gap-4">
                                <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center flex-shrink-0">
                                    <span className="text-white font-bold">3</span>
                                </div>
                                <div className="flex-1">
                                    <h3 className="text-xl font-headline font-bold text-foreground mb-2">
                                        Explore & Connect
                                    </h3>
                                    <p className="text-muted-foreground mb-3">
                                        Browse manufacturers, search products, or wait for buyer inquiries. Use our advanced filters to find exactly what you need.
                                    </p>
                                    <div className="flex flex-wrap gap-3">
                                        <Link
                                            href="/manufacturer-directory"
                                            className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
                                        >
                                            <Icon name="BuildingOfficeIcon" size={16} variant="outline" />
                                            Browse Manufacturers
                                        </Link>
                                        <Link
                                            href="/product-catalog"
                                            className="inline-flex items-center gap-2 px-4 py-2 border border-border rounded-lg hover:bg-muted transition-colors"
                                        >
                                            <Icon name="ShoppingBagIcon" size={16} variant="outline" />
                                            View Products
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="bg-card border border-border rounded-lg p-6">
                            <div className="flex items-start gap-4">
                                <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center flex-shrink-0">
                                    <span className="text-white font-bold">4</span>
                                </div>
                                <div className="flex-1">
                                    <h3 className="text-xl font-headline font-bold text-foreground mb-2">
                                        Start Trading
                                    </h3>
                                    <p className="text-muted-foreground">
                                        Send inquiries, receive quotes, negotiate terms, and establish long-term business relationships with verified partners.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            ),
        },
        {
            id: 'features',
            title: 'Platform Features',
            icon: 'SparklesIcon',
            content: (
                <div className="space-y-6">
                    <div>
                        <h2 className="text-3xl font-headline font-bold text-foreground mb-4">
                            Platform Features
                        </h2>
                        <p className="text-lg text-muted-foreground leading-relaxed mb-6">
                            Discover the powerful features that make EgypTex Gateway the leading B2B textile platform.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                        <div className="bg-card border border-border rounded-lg p-6 hover:border-primary/50 transition-colors">
                            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center mb-4">
                                <Icon name="MagnifyingGlassIcon" size={24} variant="outline" className="text-white" />
                            </div>
                            <h3 className="text-xl font-headline font-bold text-foreground mb-2">
                                Advanced Search
                            </h3>
                            <p className="text-muted-foreground mb-3">
                                Find exactly what you need with our powerful search engine featuring filters for product type, material, certifications, location, and more.
                            </p>
                            <ul className="space-y-2 text-sm text-muted-foreground">
                                <li className="flex items-center gap-2">
                                    <Icon name="CheckIcon" size={16} variant="outline" className="text-primary" />
                                    Multi-criteria filtering
                                </li>
                                <li className="flex items-center gap-2">
                                    <Icon name="CheckIcon" size={16} variant="outline" className="text-primary" />
                                    Real-time search results
                                </li>
                                <li className="flex items-center gap-2">
                                    <Icon name="CheckIcon" size={16} variant="outline" className="text-primary" />
                                    Save search preferences
                                </li>
                            </ul>
                        </div>

                        <div className="bg-card border border-border rounded-lg p-6 hover:border-primary/50 transition-colors">
                            <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-lg flex items-center justify-center mb-4">
                                <Icon name="ShieldCheckIcon" size={24} variant="outline" className="text-white" />
                            </div>
                            <h3 className="text-xl font-headline font-bold text-foreground mb-2">
                                Verified Manufacturers
                            </h3>
                            <p className="text-muted-foreground mb-3">
                                All manufacturers undergo rigorous verification to ensure quality, reliability, and compliance with international standards.
                            </p>
                            <ul className="space-y-2 text-sm text-muted-foreground">
                                <li className="flex items-center gap-2">
                                    <Icon name="CheckIcon" size={16} variant="outline" className="text-primary" />
                                    Business license verification
                                </li>
                                <li className="flex items-center gap-2">
                                    <Icon name="CheckIcon" size={16} variant="outline" className="text-primary" />
                                    Quality certifications
                                </li>
                                <li className="flex items-center gap-2">
                                    <Icon name="CheckIcon" size={16} variant="outline" className="text-primary" />
                                    Performance ratings
                                </li>
                            </ul>
                        </div>

                        <div className="bg-card border border-border rounded-lg p-6 hover:border-primary/50 transition-colors">
                            <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg flex items-center justify-center mb-4">
                                <Icon name="ChatBubbleLeftRightIcon" size={24} variant="outline" className="text-white" />
                            </div>
                            <h3 className="text-xl font-headline font-bold text-foreground mb-2">
                                Inquiry Management
                            </h3>
                            <p className="text-muted-foreground mb-3">
                                Streamline your communication with integrated inquiry management tools for efficient buyer-seller interactions.
                            </p>
                            <ul className="space-y-2 text-sm text-muted-foreground">
                                <li className="flex items-center gap-2">
                                    <Icon name="CheckIcon" size={16} variant="outline" className="text-primary" />
                                    Direct messaging system
                                </li>
                                <li className="flex items-center gap-2">
                                    <Icon name="CheckIcon" size={16} variant="outline" className="text-primary" />
                                    Quote management
                                </li>
                                <li className="flex items-center gap-2">
                                    <Icon name="CheckIcon" size={16} variant="outline" className="text-primary" />
                                    Inquiry tracking
                                </li>
                            </ul>
                        </div>

                        <div className="bg-card border border-border rounded-lg p-6 hover:border-primary/50 transition-colors">
                            <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex items-center justify-center mb-4">
                                <Icon name="ChartBarIcon" size={24} variant="outline" className="text-white" />
                            </div>
                            <h3 className="text-xl font-headline font-bold text-foreground mb-2">
                                Analytics Dashboard
                            </h3>
                            <p className="text-muted-foreground mb-3">
                                Track your performance with comprehensive analytics covering inquiries, views, engagement, and conversion metrics.
                            </p>
                            <ul className="space-y-2 text-sm text-muted-foreground">
                                <li className="flex items-center gap-2">
                                    <Icon name="CheckIcon" size={16} variant="outline" className="text-primary" />
                                    Real-time statistics
                                </li>
                                <li className="flex items-center gap-2">
                                    <Icon name="CheckIcon" size={16} variant="outline" className="text-primary" />
                                    Performance insights
                                </li>
                                <li className="flex items-center gap-2">
                                    <Icon name="CheckIcon" size={16} variant="outline" className="text-primary" />
                                    Export reports
                                </li>
                            </ul>
                        </div>

                        <div className="bg-card border border-border rounded-lg p-6 hover:border-primary/50 transition-colors">
                            <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-red-600 rounded-lg flex items-center justify-center mb-4">
                                <Icon name="GlobeAltIcon" size={24} variant="outline" className="text-white" />
                            </div>
                            <h3 className="text-xl font-headline font-bold text-foreground mb-2">
                                Multi-Language Support
                            </h3>
                            <p className="text-muted-foreground mb-3">
                                Access the platform in your preferred language with full support for English, Arabic, French, German, and Spanish.
                            </p>
                            <ul className="space-y-2 text-sm text-muted-foreground">
                                <li className="flex items-center gap-2">
                                    <Icon name="CheckIcon" size={16} variant="outline" className="text-primary" />
                                    5 languages supported
                                </li>
                                <li className="flex items-center gap-2">
                                    <Icon name="CheckIcon" size={16} variant="outline" className="text-primary" />
                                    RTL support for Arabic
                                </li>
                                <li className="flex items-center gap-2">
                                    <Icon name="CheckIcon" size={16} variant="outline" className="text-primary" />
                                    Localized content
                                </li>
                            </ul>
                        </div>

                        <div className="bg-card border border-border rounded-lg p-6 hover:border-primary/50 transition-colors">
                            <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-teal-600 rounded-lg flex items-center justify-center mb-4">
                                <Icon name="DocumentTextIcon" size={24} variant="outline" className="text-white" />
                            </div>
                            <h3 className="text-xl font-headline font-bold text-foreground mb-2">
                                Product Catalog
                            </h3>
                            <p className="text-muted-foreground mb-3">
                                Browse extensive product catalogs with detailed specifications, images, videos, tiered pricing in EGP, and sample service availability.
                            </p>
                            <ul className="space-y-2 text-sm text-muted-foreground">
                                <li className="flex items-center gap-2">
                                    <Icon name="CheckIcon" size={16} variant="outline" className="text-primary" />
                                    High-quality images & videos
                                </li>
                                <li className="flex items-center gap-2">
                                    <Icon name="CheckIcon" size={16} variant="outline" className="text-primary" />
                                    Tiered pricing in EGP
                                </li>
                                <li className="flex items-center gap-2">
                                    <Icon name="CheckIcon" size={16} variant="outline" className="text-primary" />
                                    8 textile-specific categories
                                </li>
                                <li className="flex items-center gap-2">
                                    <Icon name="CheckIcon" size={16} variant="outline" className="text-primary" />
                                    Sample service pricing
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            ),
        },
        {
            id: 'manufacturer-guide',
            title: 'Manufacturer Guide',
            icon: 'BuildingOfficeIcon',
            content: (
                <div className="space-y-6">
                    <div>
                        <h2 className="text-3xl font-headline font-bold text-foreground mb-4">
                            Manufacturer Guide
                        </h2>
                        <p className="text-lg text-muted-foreground leading-relaxed mb-6">
                            Everything you need to know about showcasing your manufacturing business on EgypTex Gateway.
                        </p>
                    </div>

                    <div className="bg-gradient-to-r from-primary/5 to-primary/10 border border-primary/20 rounded-lg p-6 mb-6">
                        <h3 className="text-xl font-headline font-bold text-foreground mb-3">
                            Why Join EgypTex Gateway?
                        </h3>
                        <div className="grid md:grid-cols-3 gap-4">
                            <div className="flex items-start gap-3">
                                <Icon name="GlobeAltIcon" size={20} variant="solid" className="text-primary mt-1" />
                                <div>
                                    <h4 className="font-semibold text-foreground mb-1">Global Reach</h4>
                                    <p className="text-sm text-muted-foreground">Connect with buyers worldwide</p>
                                </div>
                            </div>
                            <div className="flex items-start gap-3">
                                <Icon name="ChartBarIcon" size={20} variant="solid" className="text-primary mt-1" />
                                <div>
                                    <h4 className="font-semibold text-foreground mb-1">Grow Your Business</h4>
                                    <p className="text-sm text-muted-foreground">Increase visibility and sales</p>
                                </div>
                            </div>
                            <div className="flex items-start gap-3">
                                <Icon name="ShieldCheckIcon" size={20} variant="solid" className="text-primary mt-1" />
                                <div>
                                    <h4 className="font-semibold text-foreground mb-1">Build Trust</h4>
                                    <p className="text-sm text-muted-foreground">Verified manufacturer badge</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="space-y-4">
                        <div className="bg-card border border-border rounded-lg p-6">
                            <h3 className="text-xl font-headline font-bold text-foreground mb-3 flex items-center gap-2">
                                <Icon name="UserCircleIcon" size={24} variant="outline" className="text-primary" />
                                Setting Up Your Profile
                            </h3>
                            <div className="space-y-3 text-muted-foreground">
                                <p>Create a compelling manufacturer profile that attracts buyers:</p>
                                <ul className="space-y-2 ml-4">
                                    <li className="flex items-start gap-2">
                                        <span className="text-primary mt-1">•</span>
                                        <span><strong>Company Information:</strong> Provide detailed business information including year established, employee count, and production capacity</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="text-primary mt-1">•</span>
                                        <span><strong>Certifications:</strong> Upload ISO, GOTS, OEKO-TEX, and other relevant certifications</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="text-primary mt-1">•</span>
                                        <span><strong>Product Categories:</strong> Select all product categories you manufacture</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="text-primary mt-1">•</span>
                                        <span><strong>Company Images:</strong> Add high-quality photos of your facility, production lines, and team</span>
                                    </li>
                                </ul>
                            </div>
                        </div>

                        <div className="bg-card border border-border rounded-lg p-6">
                            <h3 className="text-xl font-headline font-bold text-foreground mb-3 flex items-center gap-2">
                                <Icon name="ShoppingBagIcon" size={24} variant="outline" className="text-primary" />
                                Managing Your Products
                            </h3>
                            <div className="space-y-3 text-muted-foreground">
                                <p>Showcase your products effectively:</p>
                                <ul className="space-y-2 ml-4">
                                    <li className="flex items-start gap-2">
                                        <span className="text-primary mt-1">•</span>
                                        <span><strong>Product Details:</strong> Include comprehensive specifications, materials, dimensions, and features</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="text-primary mt-1">•</span>
                                        <span><strong>High-Quality Images:</strong> Upload multiple images showing different angles and details (up to 5 images)</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="text-primary mt-1">•</span>
                                        <span><strong>Product Videos:</strong> Upload up to 3 videos per product (max 1GB each, drag & drop supported)</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="text-primary mt-1">•</span>
                                        <span><strong>Pricing Information:</strong> Set base price in EGP - tiered pricing (5% & 10% discounts) calculated automatically</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="text-primary mt-1">•</span>
                                        <span><strong>Sample Pricing:</strong> Set sample price per piece in EGP for buyer sample requests</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="text-primary mt-1">•</span>
                                        <span><strong>Textile Categories:</strong> Choose from 8 specialized categories (Yarn & Spinning, Weaving & Knitting, etc.)</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="text-primary mt-1">•</span>
                                        <span><strong>Availability Status:</strong> Keep stock levels and lead times updated</span>
                                    </li>
                                </ul>
                            </div>
                        </div>

                        <div className="bg-card border border-border rounded-lg p-6">
                            <h3 className="text-xl font-headline font-bold text-foreground mb-3 flex items-center gap-2">
                                <Icon name="ChatBubbleLeftRightIcon" size={24} variant="outline" className="text-primary" />
                                Handling Inquiries
                            </h3>
                            <div className="space-y-3 text-muted-foreground">
                                <p>Best practices for managing buyer inquiries:</p>
                                <ul className="space-y-2 ml-4">
                                    <li className="flex items-start gap-2">
                                        <span className="text-primary mt-1">•</span>
                                        <span><strong>Respond Quickly:</strong> Aim to respond within 24 hours to maintain high ratings</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="text-primary mt-1">•</span>
                                        <span><strong>Be Professional:</strong> Provide detailed, accurate information in your responses</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="text-primary mt-1">•</span>
                                        <span><strong>Use Templates:</strong> Create response templates for common questions</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="text-primary mt-1">•</span>
                                        <span><strong>Track Progress:</strong> Use the inquiry management system to track all communications</span>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    <div className="bg-card border border-primary/50 rounded-lg p-6">
                        <div className="flex items-start gap-4">
                            <Icon name="LightBulbIcon" size={32} variant="outline" className="text-primary flex-shrink-0" />
                            <div>
                                <h3 className="text-xl font-headline font-bold text-foreground mb-2">
                                    Pro Tips for Success
                                </h3>
                                <ul className="space-y-2 text-muted-foreground">
                                    <li>✓ Keep your profile and product information up to date</li>
                                    <li>✓ Respond to inquiries promptly and professionally</li>
                                    <li>✓ Upload high-quality images and detailed specifications</li>
                                    <li>✓ Maintain competitive pricing and clear MOQ requirements</li>
                                    <li>✓ Collect and showcase customer testimonials</li>
                                    <li>✓ Monitor your analytics to understand buyer behavior</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            ),
        },
        {
            id: 'buyer-guide',
            title: 'Buyer Guide',
            icon: 'ShoppingCartIcon',
            content: (
                <div className="space-y-6">
                    <div>
                        <h2 className="text-3xl font-headline font-bold text-foreground mb-4">
                            Buyer Guide
                        </h2>
                        <p className="text-lg text-muted-foreground leading-relaxed mb-6">
                            Learn how to effectively source premium Egyptian textiles through our platform.
                        </p>
                    </div>

                    <div className="space-y-4">
                        <div className="bg-card border border-border rounded-lg p-6">
                            <h3 className="text-xl font-headline font-bold text-foreground mb-3 flex items-center gap-2">
                                <Icon name="MagnifyingGlassIcon" size={24} variant="outline" className="text-primary" />
                                Finding the Right Manufacturer
                            </h3>
                            <div className="space-y-3 text-muted-foreground">
                                <p>Use our advanced search and filtering tools:</p>
                                <ul className="space-y-2 ml-4">
                                    <li className="flex items-start gap-2">
                                        <span className="text-primary mt-1">•</span>
                                        <span><strong>Search by Product:</strong> Enter specific product names or keywords</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="text-primary mt-1">•</span>
                                        <span><strong>Filter by Category:</strong> Browse manufacturers by industry category</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="text-primary mt-1">•</span>
                                        <span><strong>Check Certifications:</strong> Filter by quality certifications (ISO, GOTS, etc.)</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="text-primary mt-1">•</span>
                                        <span><strong>Location Filter:</strong> Find manufacturers in specific regions</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="text-primary mt-1">•</span>
                                        <span><strong>Production Capacity:</strong> Match manufacturers to your order volume</span>
                                    </li>
                                </ul>
                            </div>
                        </div>

                        <div className="bg-card border border-border rounded-lg p-6">
                            <h3 className="text-xl font-headline font-bold text-foreground mb-3 flex items-center gap-2">
                                <Icon name="ClipboardDocumentCheckIcon" size={24} variant="outline" className="text-primary" />
                                Evaluating Manufacturers
                            </h3>
                            <div className="space-y-3 text-muted-foreground">
                                <p>Key factors to consider when selecting a manufacturer:</p>
                                <div className="grid md:grid-cols-2 gap-4 mt-4">
                                    <div className="bg-muted/50 rounded-lg p-4">
                                        <h4 className="font-semibold text-foreground mb-2">Verification Status</h4>
                                        <p className="text-sm">Look for the verified badge indicating the manufacturer has passed our verification process</p>
                                    </div>
                                    <div className="bg-muted/50 rounded-lg p-4">
                                        <h4 className="font-semibold text-foreground mb-2">Certifications</h4>
                                        <p className="text-sm">Check for relevant quality and compliance certifications</p>
                                    </div>
                                    <div className="bg-muted/50 rounded-lg p-4">
                                        <h4 className="font-semibold text-foreground mb-2">Response Rate</h4>
                                        <p className="text-sm">Higher response rates indicate reliable communication</p>
                                    </div>
                                    <div className="bg-muted/50 rounded-lg p-4">
                                        <h4 className="font-semibold text-foreground mb-2">Product Range</h4>
                                        <p className="text-sm">Review their complete product catalog and capabilities</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="bg-card border border-border rounded-lg p-6">
                            <h3 className="text-xl font-headline font-bold text-foreground mb-3 flex items-center gap-2">
                                <Icon name="PaperAirplaneIcon" size={24} variant="outline" className="text-primary" />
                                Sending Inquiries
                            </h3>
                            <div className="space-y-3 text-muted-foreground">
                                <p>How to send effective inquiries:</p>
                                <ul className="space-y-2 ml-4">
                                    <li className="flex items-start gap-2">
                                        <span className="text-primary mt-1">•</span>
                                        <span><strong>Be Specific:</strong> Clearly describe your requirements including quantity, specifications, and timeline</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="text-primary mt-1">•</span>
                                        <span><strong>Include Details:</strong> Mention your company, intended use, and any special requirements</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="text-primary mt-1">•</span>
                                        <span><strong>Ask Questions:</strong> Inquire about MOQ, lead times, payment terms, and shipping options</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="text-primary mt-1">•</span>
                                        <span><strong>Request Samples:</strong> Ask for product samples before placing large orders</span>
                                    </li>
                                </ul>
                            </div>
                        </div>

                        <div className="bg-card border border-border rounded-lg p-6">
                            <h3 className="text-xl font-headline font-bold text-foreground mb-3 flex items-center gap-2">
                                <Icon name="HandshakeIcon" size={24} variant="outline" className="text-primary" />
                                Negotiating & Ordering
                            </h3>
                            <div className="space-y-3 text-muted-foreground">
                                <p>Best practices for successful negotiations:</p>
                                <ul className="space-y-2 ml-4">
                                    <li className="flex items-start gap-2">
                                        <span className="text-primary mt-1">•</span>
                                        <span><strong>Compare Quotes:</strong> Get quotes from multiple manufacturers for comparison</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="text-primary mt-1">•</span>
                                        <span><strong>Clarify Terms:</strong> Ensure all terms including pricing, payment, and delivery are clear</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="text-primary mt-1">•</span>
                                        <span><strong>Quality Assurance:</strong> Discuss quality control measures and inspection procedures</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <span className="text-primary mt-1">•</span>
                                        <span><strong>Documentation:</strong> Keep all communications and agreements documented</span>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    <div className="bg-gradient-to-r from-primary/5 to-primary/10 border border-primary/20 rounded-lg p-6">
                        <h3 className="text-xl font-headline font-bold text-foreground mb-3">
                            Quality Assurance
                        </h3>
                        <p className="text-muted-foreground mb-4">
                            EgypTex Gateway is committed to ensuring quality in every transaction:
                        </p>
                        <div className="grid md:grid-cols-3 gap-4">
                            <div className="flex items-start gap-2">
                                <Icon name="ShieldCheckIcon" size={20} variant="solid" className="text-primary mt-1" />
                                <div>
                                    <h4 className="font-semibold text-foreground mb-1">Verified Suppliers</h4>
                                    <p className="text-sm text-muted-foreground">All manufacturers are verified</p>
                                </div>
                            </div>
                            <div className="flex items-start gap-2">
                                <Icon name="DocumentCheckIcon" size={20} variant="solid" className="text-primary mt-1" />
                                <div>
                                    <h4 className="font-semibold text-foreground mb-1">Certification Tracking</h4>
                                    <p className="text-sm text-muted-foreground">Monitor quality certifications</p>
                                </div>
                            </div>
                            <div className="flex items-start gap-2">
                                <Icon name="ChatBubbleLeftRightIcon" size={20} variant="solid" className="text-primary mt-1" />
                                <div>
                                    <h4 className="font-semibold text-foreground mb-1">Support Team</h4>
                                    <p className="text-sm text-muted-foreground">Dedicated support available</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            ),
        },
        {
            id: 'support',
            title: 'Support & FAQ',
            icon: 'QuestionMarkCircleIcon',
            content: (
                <div className="space-y-6">
                    <div>
                        <h2 className="text-3xl font-headline font-bold text-foreground mb-4">
                            Support & FAQ
                        </h2>
                        <p className="text-lg text-muted-foreground leading-relaxed mb-6">
                            Find answers to common questions and learn how to get support.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-6 mb-8">
                        <Link
                            href="/help-center"
                            className="bg-card border border-border rounded-lg p-6 hover:border-primary/50 transition-colors group"
                        >
                            <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                                <Icon name="BookOpenIcon" size={24} variant="outline" className="text-primary" />
                            </div>
                            <h3 className="text-lg font-headline font-bold text-foreground mb-2">
                                Help Center
                            </h3>
                            <p className="text-sm text-muted-foreground">
                                Browse our comprehensive help articles and guides
                            </p>
                        </Link>

                        <Link
                            href="/contact"
                            className="bg-card border border-border rounded-lg p-6 hover:border-primary/50 transition-colors group"
                        >
                            <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                                <Icon name="EnvelopeIcon" size={24} variant="outline" className="text-primary" />
                            </div>
                            <h3 className="text-lg font-headline font-bold text-foreground mb-2">
                                Contact Us
                            </h3>
                            <p className="text-sm text-muted-foreground">
                                Get in touch with our support team
                            </p>
                        </Link>

                        <div className="bg-card border border-border rounded-lg p-6">
                            <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                                <Icon name="ChatBubbleLeftRightIcon" size={24} variant="outline" className="text-primary" />
                            </div>
                            <h3 className="text-lg font-headline font-bold text-foreground mb-2">
                                Live Chat
                            </h3>
                            <p className="text-sm text-muted-foreground mb-3">
                                Chat with our support team in real-time
                            </p>
                            <button className="text-sm text-primary hover:text-primary/80 font-medium">
                                Start Chat →
                            </button>
                        </div>
                    </div>

                    <div>
                        <h3 className="text-2xl font-headline font-bold text-foreground mb-4">
                            Frequently Asked Questions
                        </h3>
                        <div className="space-y-4">
                            <div className="bg-card border border-border rounded-lg p-6">
                                <h4 className="text-lg font-semibold text-foreground mb-2">
                                    How do I create an account?
                                </h4>
                                <p className="text-muted-foreground">
                                    Click the "Sign Up" button in the header, choose your account type (Buyer or Manufacturer), fill in your business details, and verify your email address. Once verified, you can complete your profile and start using the platform.
                                </p>
                            </div>

                            <div className="bg-card border border-border rounded-lg p-6">
                                <h4 className="text-lg font-semibold text-foreground mb-2">
                                    Is there a fee to join EgypTex Gateway?
                                </h4>
                                <p className="text-muted-foreground">
                                    Creating a basic account is free for both buyers and manufacturers. We offer premium subscription plans with additional features such as enhanced visibility, priority support, and advanced analytics. Contact our sales team for pricing details.
                                </p>
                            </div>

                            <div className="bg-card border border-border rounded-lg p-6">
                                <h4 className="text-lg font-semibold text-foreground mb-2">
                                    How are manufacturers verified?
                                </h4>
                                <p className="text-muted-foreground">
                                    Our verification process includes business license validation, facility inspection (for premium members), certification verification, and background checks. Verified manufacturers receive a badge on their profile indicating they have passed our verification standards.
                                </p>
                            </div>

                            <div className="bg-card border border-border rounded-lg p-6">
                                <h4 className="text-lg font-semibold text-foreground mb-2">
                                    Can I request product samples?
                                </h4>
                                <p className="text-muted-foreground">
                                    Yes! When sending an inquiry to a manufacturer, you can request product samples. Sample availability, pricing, and shipping terms are determined by each manufacturer individually.
                                </p>
                            </div>

                            <div className="bg-card border border-border rounded-lg p-6">
                                <h4 className="text-lg font-semibold text-foreground mb-2">
                                    What payment methods are supported?
                                </h4>
                                <p className="text-muted-foreground">
                                    EgypTex Gateway is a B2B marketplace platform. Payment terms and methods are negotiated directly between buyers and manufacturers. Common methods include bank transfers, letters of credit, and trade finance solutions.
                                </p>
                            </div>

                            <div className="bg-card border border-border rounded-lg p-6">
                                <h4 className="text-lg font-semibold text-foreground mb-2">
                                    How do I track my inquiries?
                                </h4>
                                <p className="text-muted-foreground">
                                    All inquiries are tracked in your dashboard. You can view inquiry status, responses, quotes, and communication history. Email notifications keep you updated on new responses and status changes.
                                </p>
                            </div>

                            <div className="bg-card border border-border rounded-lg p-6">
                                <h4 className="text-lg font-semibold text-foreground mb-2">
                                    What languages are supported?
                                </h4>
                                <p className="text-muted-foreground">
                                    EgypTex Gateway supports English, Arabic, French, German, and Spanish. You can switch languages using the language selector in the header. All platform features and content are available in your selected language.
                                </p>
                            </div>

                            <div className="bg-card border border-border rounded-lg p-6">
                                <h4 className="text-lg font-semibold text-foreground mb-2">
                                    How can I improve my manufacturer profile visibility?
                                </h4>
                                <p className="text-muted-foreground">
                                    Keep your profile complete and up-to-date, upload high-quality images, respond quickly to inquiries, maintain certifications, add detailed product specifications, and consider upgrading to a premium membership for enhanced visibility features.
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-gradient-to-r from-primary/5 to-primary/10 border border-primary/20 rounded-lg p-6">
                        <h3 className="text-xl font-headline font-bold text-foreground mb-3">
                            Still Have Questions?
                        </h3>
                        <p className="text-muted-foreground mb-4">
                            Our support team is here to help. Contact us through any of the following channels:
                        </p>
                        <div className="grid md:grid-cols-2 gap-4">
                            <div className="flex items-center gap-3">
                                <Icon name="EnvelopeIcon" size={20} variant="solid" className="text-primary" />
                                <div>
                                    <p className="text-sm font-semibold text-foreground">Email</p>
                                    <p className="text-sm text-muted-foreground">support@egyptexgateway.com</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                <Icon name="PhoneIcon" size={20} variant="solid" className="text-primary" />
                                <div>
                                    <p className="text-sm font-semibold text-foreground">Phone</p>
                                    <p className="text-sm text-muted-foreground">+20 10 44868081</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            ),
        },
    ];

    const currentSection = docSections.find(section => section.id === activeSection);

    return (
        <div className="min-h-screen bg-background">
            {/* Header */}
            <div className="bg-gradient-to-r from-primary to-primary/80 text-white">
                <div className="max-w-7xl mx-auto px-6 py-16">
                    <div className="flex items-center gap-3 mb-4">
                        <Icon name="BookOpenIcon" size={32} variant="outline" />
                        <h1 className="text-4xl font-headline font-bold">
                            Documentation
                        </h1>
                    </div>
                    <p className="text-xl text-white/90 max-w-3xl">
                        Everything you need to know about using EgypTex Gateway - from getting started to advanced features.
                    </p>
                </div>
            </div>

            {/* Main Content */}
            <div className="max-w-7xl mx-auto px-6 py-12">
                <div className="grid lg:grid-cols-4 gap-8">
                    {/* Sidebar Navigation */}
                    <div className="lg:col-span-1">
                        <div className="sticky top-6">
                            <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4">
                                Contents
                            </h2>
                            <nav className="space-y-1">
                                {docSections.map((section) => (
                                    <button
                                        key={section.id}
                                        onClick={() => setActiveSection(section.id)}
                                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors ${activeSection === section.id
                                            ? 'bg-primary text-white'
                                            : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                                            }`}
                                    >
                                        <Icon
                                            name={section.icon as any}
                                            size={20}
                                            variant="outline"
                                        />
                                        <span className="font-medium">{section.title}</span>
                                    </button>
                                ))}
                            </nav>

                            {/* Quick Links */}
                            <div className="mt-8 pt-8 border-t border-border">
                                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-4">
                                    Quick Links
                                </h3>
                                <div className="space-y-2">
                                    <Link
                                        href="/help-center"
                                        className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                                    >
                                        <Icon name="LifebuoyIcon" size={16} variant="outline" />
                                        Help Center
                                    </Link>
                                    <Link
                                        href="/contact"
                                        className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                                    >
                                        <Icon name="EnvelopeIcon" size={16} variant="outline" />
                                        Contact Support
                                    </Link>
                                    <Link
                                        href="/blog"
                                        className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                                    >
                                        <Icon name="NewspaperIcon" size={16} variant="outline" />
                                        Blog & Updates
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Content Area */}
                    <div className="lg:col-span-3">
                        <div className="prose prose-lg max-w-none">
                            {currentSection?.content}
                        </div>

                        {/* Navigation Footer */}
                        <div className="flex items-center justify-between mt-12 pt-8 border-t border-border">
                            <div>
                                {docSections.findIndex(s => s.id === activeSection) > 0 && (
                                    <button
                                        onClick={() => {
                                            const currentIndex = docSections.findIndex(s => s.id === activeSection);
                                            setActiveSection(docSections[currentIndex - 1].id);
                                            window.scrollTo({ top: 0, behavior: 'smooth' });
                                        }}
                                        className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
                                    >
                                        <Icon name="ArrowLeftIcon" size={16} variant="outline" />
                                        <span>Previous</span>
                                    </button>
                                )}
                            </div>
                            <div>
                                {docSections.findIndex(s => s.id === activeSection) < docSections.length - 1 && (
                                    <button
                                        onClick={() => {
                                            const currentIndex = docSections.findIndex(s => s.id === activeSection);
                                            setActiveSection(docSections[currentIndex + 1].id);
                                            window.scrollTo({ top: 0, behavior: 'smooth' });
                                        }}
                                        className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
                                    >
                                        <span>Next</span>
                                        <Icon name="ArrowRightIcon" size={16} variant="outline" />
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DocsPage;
